/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SystemCleanupFormPage.tsx
 🗓️ Created: 2025-12-20
 🧩 Description:
    System Cleanup form page for editing a tenant's cleanup settings.
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Play, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../../lib/api';
import { Toggle } from '../../../../components/ui/Toggle';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useI18n } from '../../../../hooks/useI18n';
import { FormBox } from '../../../../components/ui/FormBox';
import { FixedActionBar } from '../../../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../../../components/layouts';
import SimpleTooltip from '../../../../components/ui/SimpleTooltip';

// Schedule options
const SCHEDULE_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
];

// Audio quality options
const AUDIO_QUALITY_OPTIONS = [
    { value: 'good', label: 'Good (16k)' },
    { value: 'high', label: 'High (32k)' },
    { value: 'lossless', label: 'Lossless (WAV)' }
];

// Default form data
const DEFAULT_FORM_DATA = {
    deleteOldestCdr: '',
    deleteOldestRecordings: '180',
    deleteOldestVoicemails: '60',
    deleteShortRecordings: '1',
    deleteOldestLogEvents: '',
    deleteOldestSms: '',
    schedule: 'none',
    audioQuality: 'good',
    convertRecordings: false,
    enabled: false,
};

export default function SystemCleanupFormPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { tenantId } = useParams<{ tenantId: string }>();
    const { showSuccess: notifySuccess, showError: notifyError } = useNotifications();

    const [isSaving, setIsSaving] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [tenantName, setTenantName] = useState('');

    // Form State
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

    // Load settings on mount
    useEffect(() => {
        const loadSettings = async () => {
            if (!tenantId) {
                navigate('/admin/maintenance/system');
                return;
            }

            setIsLoading(true);
            try {
                // Load tenant info and settings
                const [settingsResponse, tenantResponse] = await Promise.all([
                    api.get(`/api/system/cleanup-settings/${tenantId}`),
                    api.get(`/api/tenants/${tenantId}`)
                ]);

                if (tenantResponse.data.success && tenantResponse.data.data) {
                    setTenantName(tenantResponse.data.data.name || '');
                }

                if (settingsResponse.data.success && settingsResponse.data.data) {
                    const config = settingsResponse.data.data;
                    setFormData({
                        deleteOldestCdr: config.deleteOldestCdr?.toString() || '',
                        deleteOldestRecordings: config.deleteOldestRecordings?.toString() || '',
                        deleteOldestVoicemails: config.deleteOldestVoicemails?.toString() || '',
                        deleteShortRecordings: config.deleteShortRecordings?.toString() || '',
                        deleteOldestLogEvents: config.deleteOldestLogEvents?.toString() || '',
                        deleteOldestSms: config.deleteOldestSms?.toString() || '',
                        schedule: config.schedule || 'none',
                        audioQuality: config.audioQuality || 'good',
                        convertRecordings: config.convertRecordings || false,
                        enabled: config.enabled || false,
                    });
                }
            } catch (err) {
                console.error('Failed to load settings', err);
                notifyError(t('systemCleanup.messages.loadError'));
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, [tenantId, navigate, notifyError, t]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (key: string, value: any) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async (action?: 'save' | 'save-and-close') => {
        if (!tenantId) return;

        setIsSaving(true);
        try {
            const config = {
                deleteOldestCdr: formData.deleteOldestCdr ? parseInt(formData.deleteOldestCdr, 10) : null,
                deleteOldestRecordings: formData.deleteOldestRecordings ? parseInt(formData.deleteOldestRecordings, 10) : null,
                deleteOldestVoicemails: formData.deleteOldestVoicemails ? parseInt(formData.deleteOldestVoicemails, 10) : null,
                deleteShortRecordings: formData.deleteShortRecordings ? parseInt(formData.deleteShortRecordings, 10) : null,
                deleteOldestLogEvents: formData.deleteOldestLogEvents ? parseInt(formData.deleteOldestLogEvents, 10) : null,
                deleteOldestSms: formData.deleteOldestSms ? parseInt(formData.deleteOldestSms, 10) : null,
                schedule: formData.schedule,
                audioQuality: formData.audioQuality,
                convertRecordings: formData.convertRecordings,
                enabled: formData.enabled,
            };

            await api.put(`/api/system/cleanup-settings/${tenantId}`, config);
            notifySuccess(t('systemCleanup.messages.saveSuccess'));

            // Navigate back to list if save-and-close
            if (action === 'save-and-close') {
                navigate('/admin/maintenance/system');
            }
        } catch (error) {
            notifyError(t('systemCleanup.messages.saveError'));
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleExecute = async () => {
        if (!tenantId) return;

        setIsExecuting(true);
        try {
            await api.post('/api/system/cleanup', { tenantId });
            notifySuccess(t('systemCleanup.messages.executeSuccess'));
        } catch (error) {
            notifyError(t('systemCleanup.messages.executeError'));
            console.error(error);
        } finally {
            setIsExecuting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/maintenance/system');
    };

    const handleBack = () => {
        navigate('/admin/maintenance/system');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Settings className="h-5 w-5 text-primary" />}
                title={`${t('systemCleanup.title')} - ${tenantName}`}
                backButtonText={t('systemCleanup.backToList')}
                onBack={handleBack}
            />

            <div className="mt-8 space-y-4">
                <FormBox
                    title={t('systemCleanup.sections.cleanupSettings')}
                    icon={<Settings className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Row 1: Delete Oldest CDR | Input | Delete Oldest Recordings | Input */}
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteOldestCdr')}
                            title={t('systemCleanup.fields.deleteOldestCdr')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteOldestCdr')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteOldestCdr}
                            onChange={(e) => handleChange('deleteOldestCdr', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.days')}
                        />
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteOldestRecordings')}
                            title={t('systemCleanup.fields.deleteOldestRecordings')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteOldestRecordings')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteOldestRecordings}
                            onChange={(e) => handleChange('deleteOldestRecordings', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.days')}
                        />

                        {/* Row 2: Delete Oldest Voicemails | Input | Delete Short Recordings | Input */}
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteOldestVoicemails')}
                            title={t('systemCleanup.fields.deleteOldestVoicemails')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteOldestVoicemails')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteOldestVoicemails}
                            onChange={(e) => handleChange('deleteOldestVoicemails', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.days')}
                        />
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteShortRecordings')}
                            title={t('systemCleanup.fields.deleteShortRecordings')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteShortRecordings')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteShortRecordings}
                            onChange={(e) => handleChange('deleteShortRecordings', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.seconds')}
                        />

                        {/* Row 3: Delete Oldest Log Events | Input | Delete Oldest SMS | Input */}
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteOldestLogEvents')}
                            title={t('systemCleanup.fields.deleteOldestLogEvents')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteOldestLogEvents')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteOldestLogEvents}
                            onChange={(e) => handleChange('deleteOldestLogEvents', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.days')}
                        />
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.deleteOldestSms')}
                            title={t('systemCleanup.fields.deleteOldestSms')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.deleteOldestSms')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={formData.deleteOldestSms}
                            onChange={(e) => handleChange('deleteOldestSms', e.target.value)}
                            placeholder={t('systemCleanup.placeholders.days')}
                        />

                        {/* Row 4: Schedule | Select | Audio Quality | Select */}
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.schedule')}
                            title={t('systemCleanup.fields.schedule')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.schedule')}</Label>
                        </SimpleTooltip>
                        <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={formData.schedule}
                            onChange={(e) => handleChange('schedule', e.target.value)}
                        >
                            {SCHEDULE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {t(`systemCleanup.options.schedule.${opt.value}`)}
                                </option>
                            ))}
                        </select>
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.audioQuality')}
                            title={t('systemCleanup.fields.audioQuality')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.audioQuality')}</Label>
                        </SimpleTooltip>
                        <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={formData.audioQuality}
                            onChange={(e) => handleChange('audioQuality', e.target.value)}
                        >
                            {AUDIO_QUALITY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {t(`systemCleanup.options.audioQuality.${opt.value}`)}
                                </option>
                            ))}
                        </select>

                        {/* Row 5: Convert Recordings | Toggle | Enabled | Toggle */}
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.convertRecordings')}
                            title={t('systemCleanup.fields.convertRecordings')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.convertRecordings')}</Label>
                        </SimpleTooltip>
                        <div>
                            <Toggle
                                value={formData.convertRecordings}
                                onChange={(pressed) => handleChange('convertRecordings', pressed)}
                            />
                        </div>
                        <SimpleTooltip
                            content={t('systemCleanup.tooltips.enabled')}
                            title={t('systemCleanup.fields.enabled')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left">{t('systemCleanup.fields.enabled')}</Label>
                        </SimpleTooltip>
                        <div>
                            <Toggle
                                value={formData.enabled}
                                onChange={(pressed) => handleChange('enabled', pressed)}
                            />
                        </div>
                    </div>
                </FormBox>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSave}
                onCancel={handleCancel}
                isSaving={isSaving}
                useSaveDropdown={true}
                saveDropdownStorageKey="systemCleanup"
                availableSaveActions={['save', 'save-and-close']}
                cancelText={t('common.cancel')}
            >
                {/* Execute Now button on the left side */}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleExecute}
                    disabled={isExecuting || isSaving}
                    className="flex items-center gap-2"
                >
                    {isExecuting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Play className="h-4 w-4" />
                    )}
                    {t('systemCleanup.actions.executeNow')}
                </Button>
            </FixedActionBar>
        </div>
    );
}
